import { useApiClient, useFetch } from '@marageti/z4-lib';
import { TripMatch, Review, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';

const today = new Date();

// This hook takes a trip match and returns;
// - reviewPrompt: {
//   show: boolean - whether or not the trip match needs a review
//   emptyReview: Review | undefined - the empty review for the trip match if it exists
// }
// - reviews: Review[] - the reviews for the trip match
const useReviewPromptTripMatch = (
  tripMatch: Pick<TripMatch, 'id' | 'status' | 'returnDate' | 'travelers'>,
  hideReviewRequest?: boolean,
) => {
  const apiClient = useApiClient();
  const tripMatchReviews = useFetch<Review[]>(
    apiClient.tripMatchesClient.getTripMatchReviews,
    [tripMatch.id],
    true,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  // Show the review prompt if
  // 1. the trip match is sold,
  // 2. the trip match is in the past,
  // 3. the traveler has not yet reviewed the trip match OR the traveler has not yet completed the review for the trip match (backend will create blank reviews for sold trip matches)
  // 4. hideReviewRequest flag is not true
  const reviews = tripMatchReviews.data ?? [];
  const isTripMatchSold = tripMatch.status === TripMatchStatus.MatchSold;
  const returnDate = new Date(tripMatch.returnDate);
  const isTripMatchInPast = returnDate < today;
  const hasReview = Boolean(reviews && reviews.length > 0);
  const existingEmptyReview = reviews.find((review: Review) => !review.rating);
  const isReviewRequestHidden = hideReviewRequest ?? false;
  const showReviewPrompt = !tripMatchReviews.isLoading
    && isTripMatchSold
    && isTripMatchInPast
    && (!hasReview || !!existingEmptyReview)
    && !isReviewRequestHidden;

  return {
    reviewPrompt: {
      show: showReviewPrompt,
      emptyReview: existingEmptyReview,
    },
    reviews,
  };
};

export default useReviewPromptTripMatch;
